import { cn } from '@/utils/cn'

interface WeekSelectorProps {
  selected: number[]
  onToggle: (week: number) => void
  onSetWeeks: (weeks: number[]) => void
}

const range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i)

const QUARTERS = [
  { label: 'Q1', weeks: range(1, 13) },
  { label: 'Q2', weeks: range(14, 26) },
  { label: 'Q3', weeks: range(27, 39) },
  { label: 'Q4', weeks: range(40, 52) },
]

const ALL_WEEKS = range(1, 52)
const LEAP_WEEK = 53

/**
 * Pick which of the 52 fiscal weeks the template applies to (by POG Set Date).
 * Week 53 is offered separately — it only exists in 53-week (leap) fiscal years.
 */
export function WeekSelector({
  selected,
  onToggle,
  onSetWeeks,
}: WeekSelectorProps) {
  const selectedSet = new Set(selected)
  const leapSelected = selectedSet.has(LEAP_WEEK)

  const toggleQuarter = (weeks: number[]) => {
    const allSelected = weeks.every(w => selectedSet.has(w))
    if (allSelected) {
      onSetWeeks(selected.filter(w => !weeks.includes(w)))
    } else {
      onSetWeeks(Array.from(new Set([...selected, ...weeks])))
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Quick select:</span>
        <button
          type="button"
          onClick={() => onSetWeeks(ALL_WEEKS)}
          className="rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted cursor-pointer"
        >
          All 52
        </button>
        <button
          type="button"
          onClick={() => onSetWeeks([])}
          className="rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted cursor-pointer"
        >
          Clear
        </button>
        {QUARTERS.map(q => (
          <button
            key={q.label}
            type="button"
            onClick={() => toggleQuarter(q.weeks)}
            className="rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted cursor-pointer"
          >
            {q.label}
          </button>
        ))}
      </div>

      <div className="space-y-1.5">
        {QUARTERS.map(q => (
          <div key={q.label} className="flex items-center gap-2">
            <span className="w-7 shrink-0 text-xs font-semibold text-muted-foreground">
              {q.label}
            </span>
            <div className="grid flex-1 grid-cols-[repeat(13,minmax(0,1fr))] gap-1">
              {q.weeks.map(week => {
                const isSelected = selectedSet.has(week)
                return (
                  <button
                    key={week}
                    type="button"
                    onClick={() => onToggle(week)}
                    className={cn(
                      'flex h-8 items-center justify-center rounded-md border text-xs font-medium tabular-nums transition-colors cursor-pointer',
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground hover:border-primary/40 hover:bg-muted/50'
                    )}
                  >
                    {week}
                  </button>
                )
              })}
            </div>
          </div>
        ))}

        <div className="flex items-center gap-2">
          <span className="w-7 shrink-0 text-xs font-semibold text-muted-foreground">
            53*
          </span>
          <div className="grid flex-1 grid-cols-[repeat(13,minmax(0,1fr))] gap-1">
            <button
              type="button"
              onClick={() => onToggle(LEAP_WEEK)}
              title="Week 53 — only applies in leap (53-week) fiscal years"
              className={cn(
                'flex h-8 items-center justify-center rounded-md border text-xs font-medium tabular-nums transition-colors cursor-pointer',
                leapSelected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-dashed border-border bg-card text-foreground hover:border-primary/40 hover:bg-muted/50'
              )}
            >
              53*
            </button>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        * Week 53 only exists in leap (53-week) fiscal years; it&rsquo;s ignored
        for standard 52-week years.
      </p>
    </div>
  )
}
